// NOTE: Web Audio API generated chime. Two short stacked sine partials with a
// quick fade so the result lands as a soft "ding" without bundling a sound
// asset. Generated lazily — we don't create the AudioContext until the first
// notification fires so we never trip the browser's autoplay policy at boot.

import type {
  IScheduleChimePartialParams,
  IWindowWithWebkitAudio,
} from "./types";

const NOTIFICATION_CHIME_PARTIALS = [
  // NOTE: [frequencyHz, startOffsetMs, durationMs, peakGain]
  [880, 0, 220, 0.18],
  [1320, 60, 220, 0.12],
] as const;

const FADE_IN_MS = 8;
const FADE_OUT_MS = 60;

let audioContext: AudioContext | null = null;
let isUnlockListenersAttached = false;
// NOTE: When a notification arrives before the user's first gesture, the
// AudioContext is still suspended and the chime is dropped. Setting this flag
// lets the unlock listener replay one chime the moment audio becomes usable
// — so the user still hears the very first post-refresh notification when
// they finally click the bell (or anywhere else).
let hasPendingChime = false;

const getAudioContext = (): AudioContext | null => {
  if (typeof globalThis === "undefined") {
    return null;
  }
  // NOTE: Safari exposes `webkitAudioContext` instead of the standard
  // `AudioContext`. Cast through `unknown` so we can probe both names without
  // widening the global type for the rest of the app.
  const win = globalThis as unknown as IWindowWithWebkitAudio;
  const Ctor = win.AudioContext ?? win.webkitAudioContext;
  if (!Ctor) {
    return null;
  }
  audioContext ??= new Ctor();
  return audioContext;
};

// NOTE: Browser autoplay policy: an AudioContext stays in "suspended" state
// until the tab receives a real user gesture (click / keydown / touch). If a
// notification arrives before the user has interacted with this tab,
// `context.resume()` is rejected and the chime is silently dropped — this is
// why a logged-in tab/window that the user hasn't clicked into stays silent
// even though the badge updates. Gesture activation is per-Window (each
// browser window/tab has its own state), so clicking in window A does NOT
// unlock audio in window B. We attach one-shot listeners covering every
// gesture type the autoplay policy accepts (Chrome/Safari accept pointerdown,
// keydown, and touchstart; Firefox is similar). Tab focus / visibilitychange
// are NOT user gestures per spec — even if we resume there it won't actually
// produce sound until a real gesture lands.
const UNLOCK_EVENT_NAMES = [
  "pointerdown",
  "click",
  "keydown",
  "touchstart",
] as const;

export const primeAudioContextOnUserGesture = (): void => {
  if (isUnlockListenersAttached || typeof document === "undefined") {
    return;
  }
  isUnlockListenersAttached = true;

  // NOTE: Eager attempt — this hook only mounts AFTER `isAuthenticated`
  // flips true, which means the user already clicked something to log in.
  // That gives the document "sticky activation" (one-way flag, persists for
  // the document's lifetime in an SPA), and Chrome / Firefox allow
  // AudioContext.resume() under sticky activation. If it works, audio is
  // unlocked here and the listener fallback below is never needed. If it
  // doesn't (e.g. token-based silent sign-in, or browser policy variation),
  // the listeners catch the next gesture.
  const eagerContext = getAudioContext();
  if (eagerContext?.state === "suspended") {
    void eagerContext.resume().catch(() => {});
  }

  const flushPendingChime = (context: AudioContext): void => {
    if (!hasPendingChime || context.state !== "running") {
      return;
    }
    hasPendingChime = false;
    scheduleChime(context);
  };

  const unlock = (): void => {
    const context = getAudioContext();
    if (context?.state === "suspended") {
      void context
        .resume()
        .then(() => flushPendingChime(context))
        .catch(() => {});
    } else if (context) {
      flushPendingChime(context);
    }
    for (const eventName of UNLOCK_EVENT_NAMES) {
      document.removeEventListener(eventName, unlock);
    }
  };

  for (const eventName of UNLOCK_EVENT_NAMES) {
    // NOTE: { once: true } so each handler self-removes after the first
    // gesture — the explicit removeEventListener loop above is a belt-and-
    // braces cleanup for the other event types when ANY one of them fires.
    document.addEventListener(eventName, unlock, { once: true });
  }
};

const scheduleChimePartial = ({
  context,
  frequency,
  startOffsetMs,
  durationMs,
  peakGain,
}: IScheduleChimePartialParams): void => {
  const startAt = context.currentTime + startOffsetMs / 1000;
  const stopAt = startAt + durationMs / 1000;

  const oscillator = context.createOscillator();
  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(frequency, startAt);

  const gain = context.createGain();
  // NOTE: Quick fade-in to avoid an audible "tick" at the attack edge, then a
  // slightly longer exponential decay so the chime tails off naturally.
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(peakGain, startAt + FADE_IN_MS / 1000);
  gain.gain.exponentialRampToValueAtTime(0.0001, stopAt - FADE_OUT_MS / 1000);

  oscillator.connect(gain).connect(context.destination);
  oscillator.start(startAt);
  oscillator.stop(stopAt);
};

const scheduleChime = (context: AudioContext): void => {
  for (const [
    frequency,
    startOffsetMs,
    durationMs,
    peakGain,
  ] of NOTIFICATION_CHIME_PARTIALS) {
    scheduleChimePartial({
      context,
      frequency,
      startOffsetMs,
      durationMs,
      peakGain,
    });
  }
};

// NOTE: Fire-and-forget. Safe to call on every `notification:created` event —
// failures (missing Web Audio support, autoplay block before any user gesture,
// suspended context) are swallowed silently.
export const playNotificationSound = (): void => {
  try {
    const context = getAudioContext();
    if (!context) {
      return;
    }

    // NOTE: If a notification arrives before any user gesture, resume()
    // rejects per browser autoplay policy. We schedule only AFTER resume
    // succeeds so `context.currentTime` is fresh — otherwise oscillators
    // queued against a suspended-then-resumed context can land in the past
    // and produce no audible output. If resume keeps failing (no gesture
    // has happened yet), we mark `hasPendingChime` so the unlock listener
    // can replay the chime as soon as the user finally interacts.
    if (context.state === "suspended") {
      hasPendingChime = true;
      void context
        .resume()
        .then(() => {
          if (context.state === "running" && hasPendingChime) {
            hasPendingChime = false;
            scheduleChime(context);
          }
        })
        .catch(() => {});
      return;
    }

    scheduleChime(context);
  } catch {
    // NOTE: Audio failures must never break the WS handler that called us.
  }
};
